import { useQueries } from '@tanstack/react-query';

import { getHydraUIAPIQueryOptions, hydraUiApi, useHydraUIAPIQuery } from '@/services/api';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';

const SYSTEM_TRIGGER_NAMES = new Set([
  'password_reset',
  'team_member_invite',
  'invite_existing_user',
  'signup_verification',
  'signup_with_existing_account',
  'copilot_message',
]);

export function useSendTransactionalStep(enabled = true, workspaceIdOverride?: number) {
  const urlWorkspaceId = useWorkspaceIdFromCurrentUrl() ?? 0;
  const workspaceId = workspaceIdOverride ?? urlWorkspaceId;

  const { data, isLoading: isLoadingMessages } = useHydraUIAPIQuery({
    queryKey: ['getTransactionalMessages', { params: { environment_id: workspaceId }, query: {} }],
    queryFn: ({ signal }) =>
      hydraUiApi.transactional.getTransactionalMessages({
        params: { path: { environment_id: String(workspaceId) } },
        signal,
      }),
    queryConfig: { enabled: enabled && workspaceId > 0 },
  });

  const messages = data?.transactional_messages ?? [];

  const userMessages = messages.filter((m) => m.trigger_name == null || !SYSTEM_TRIGGER_NAMES.has(m.trigger_name));

  const metricsQueries = useQueries({
    queries: userMessages.map((m) =>
      getHydraUIAPIQueryOptions({
        queryKey: [
          'getTransactionalMessageMetrics',
          { params: { environment_id: workspaceId, transactional_message_id: m.id } },
        ],
        queryFn: ({ signal }) =>
          hydraUiApi.transactional.getTransactionalMessagesMetrics({
            params: { path: { environment_id: String(workspaceId), transactional_message_id: String(m.id) } },
            signal,
          }),
        queryConfig: { enabled: enabled && workspaceId > 0 && userMessages.length > 0 },
      }),
    ),
  });

  const isLoadingMetrics = metricsQueries.some((q) => q.isLoading);
  const isLoading = isLoadingMessages || isLoadingMetrics;
  const hasSentTransactional = metricsQueries.some((q) => (q.data?.metric.total_sent ?? 0) > 0);

  return { hasSentTransactional, isLoading };
}
